import React, { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { useOwnerMode } from './OwnerModeContext';
import { LineChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis, BarChart, Bar, Cell } from 'recharts';
import { TrendingUp, Users, DollarSign, Target, Sparkles, ArrowUpRight, Clock } from 'lucide-react';
import { Link } from 'react-router-dom';
import PageContainer from './kit/PageContainer';
import { Button } from '@/components/ui/button';
import { BRAND } from './kit/tokens';
import { errorMessage } from './errors';
import { brandAssetUrl } from '../branding';

const fmt$ = (n) => (n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

// Mortgage bars and recent rows carry the tenant's own stage labels and colors
// (`stage_bars`); these maps are the real-estate transaction stages only.
const stageColors = {
  prospect: '#94a3b8', showing: '#a3a3a3', offer_out: '#3b82f6', offer_accepted: '#6366f1',
  under_contract: '#8b5cf6', inspection: '#f59e0b', appraisal: '#f97316', financing: '#facc15',
  clear_to_close: BRAND.accentBright, closed: '#22c55e', cancelled: '#ef4444', fell_through: '#dc2626',
};
const stageLabels = {
  prospect: 'Prospect', showing: 'Showing', offer_out: 'Offer Out', offer_accepted: 'Accepted',
  under_contract: 'Contract', inspection: 'Insp.', appraisal: 'Appraisal', financing: 'Financing',
  clear_to_close: 'CTC', closed: 'Closed', cancelled: 'Cancelled', fell_through: 'Fell Through',
};

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [noWorkspace, setNoWorkspace] = useState(false);
  const { effectiveProductType } = useOwnerMode();

  const load = useCallback(() => {
    setLoadError(null);
    api.get(`/dashboard/summary?product_type=${effectiveProductType}`)
      .then(r => setData(r.data))
      .catch(e => {
        setData(null);
        setLoadError(errorMessage(e, 'Could not load the dashboard.'));
      });
  }, [effectiveProductType]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // Cover photo lives on the tenant doc — fetch once and cache
    api.get('/tenants/mine')
      .then(r => { setTenant(r.data); setNoWorkspace(false); })
      .catch(err => {
        setTenant(null);
        // Ticket 55: 404 { detail: { code: 'no_active_tenant' } } — user has no workspace.
        if (err?.response?.status === 404 && err?.response?.data?.detail?.code === 'no_active_tenant') {
          setNoWorkspace(true);
        }
      });
  }, []);

  if (noWorkspace) {
    return (
      <PageContainer
        title="No workspace yet"
        subtitle="Your account isn't attached to a workspace, so there's nothing to show here."
        data-testid="dashboard-no-workspace"
      />
    );
  }

  if (loadError) {
    return (
      <div className="p-10 text-center" role="alert" data-testid="dashboard-error">
        <p className="text-slate-700">{loadError}</p>
        <Button variant="accent" className="mt-4" onClick={load} data-testid="dashboard-retry">Retry</Button>
      </div>
    );
  }

  if (!data) return <div className="p-10 text-slate-500">Loading...</div>;

  const c = data.copy || {};
  // Mortgage: the tenant's stages in board order, with their labels and colors.
  // Real estate: the transaction stage keys, labelled here.
  const stageData = data.stage_bars
    ? data.stage_bars.map(b => ({ name: b.label, value: b.count, color: b.color || '#94a3b8' }))
    : Object.entries(data.by_status || {}).map(([k, v]) => ({
      name: stageLabels[k] || k,
      value: v,
      color: stageColors[k] || '#94a3b8',
    }));
  const labelColors = Object.fromEntries((data.stage_bars || []).map(b => [b.label, b.color]));

  const cards = [
    { label: c.pipeline_label || 'Active Pipeline', value: data.active_pipeline, icon: Target, color: BRAND.accent, hint: c.pipeline_hint || 'Open deals' },
    { label: c.volume_label || 'Loan Volume', value: fmt$(data.total_volume), icon: DollarSign, color: BRAND.navy, hint: c.volume_hint || 'Active + closed' },
    { label: c.leads_label || 'Total Leads', value: data.total_leads, icon: Users, color: '#3b82f6', hint: `${data.conversion_rate}% conversion` },
    { label: 'Open Tasks', value: data.open_tasks, icon: Clock, color: '#f59e0b', hint: 'Requiring action', href: '/portal/tasks' },
  ];

  const items = data.recent_items || data.recent_leads || [];

  const askAudrey = (
    <Button asChild variant="accent">
      <Link to="/portal/ai"><Sparkles size={15} /> Ask Audrey</Link>
    </Button>
  );

  // With a cover photo the title/subtitle/CTA render inside the hero instead
  const showFallbackHeader = !tenant?.cover_photo_url;

  return (
    <PageContainer
      title={showFallbackHeader ? (c.title || 'Dashboard') : undefined}
      subtitle={showFallbackHeader ? (c.subtitle || 'Your business at a glance.') : undefined}
      actions={showFallbackHeader ? askAudrey : undefined}
      titleProps={{ 'data-testid': 'dashboard-title' }}
      subtitleProps={{ 'data-testid': 'dashboard-subtitle' }}
    >
      {/* Cover photo hero — only rendered if the tenant has uploaded one */}
      {tenant?.cover_photo_url && (
        <div
          className="relative rounded-2xl overflow-hidden h-40 md:h-56 shadow-md"
          data-testid="dashboard-cover-hero"
        >
          <img src={brandAssetUrl(tenant.cover_photo_url)} alt="" className="w-full h-full object-cover" />
          {/* Gradient overlay so the title reads on any photo */}
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(7,11,95,0.25) 0%, rgba(7,11,95,0.75) 100%)' }} />
          <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-7">
            <div className="text-[10px] uppercase tracking-widest text-white/70 font-semibold">{tenant.name}</div>
            <h1 className="font-serif text-3xl md:text-4xl font-bold text-white mt-1" data-testid="dashboard-title">{c.title || 'Dashboard'}</h1>
            <p className="text-sm text-white/80 mt-0.5" data-testid="dashboard-subtitle">{c.subtitle || 'Your business at a glance.'}</p>
          </div>
          <div className="absolute top-4 right-4">
            {askAudrey}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((card) => {
          const Icon = card.icon;
          // A card with an href is a real link (keyboard reachable); the rest stay plain.
          const Card = card.href ? Link : 'div';
          return (
            <Card
              key={card.label}
              {...(card.href ? { to: card.href } : {})}
              className={`block bg-white rounded-2xl p-5 shadow-sm border border-slate-100 hover:-translate-y-0.5 transition-transform${card.href ? ' focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent' : ''}`}
              data-testid={`kpi-${card.label.replace(/\s+/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center justify-between">
                <div className="w-11 h-11 rounded-xl flex items-center justify-center" style={{ background: card.color + '15' }}>
                  <Icon size={20} style={{ color: card.color }} />
                </div>
                <span className="text-xs text-slate-400">{card.hint}</span>
              </div>
              <div className="mt-4 text-3xl font-serif font-bold text-brand-navy">{card.value}</div>
              <div className="text-sm text-slate-500 mt-0.5">{card.label}</div>
            </Card>
          );
        })}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Trend */}
        <div className="lg:col-span-2 bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="font-serif font-semibold text-lg text-brand-navy">
                {effectiveProductType === 'real_estate' ? 'Transaction Trend' : 'Lead Trend'}
              </div>
              <div className="text-xs text-slate-500">
                {effectiveProductType === 'real_estate' ? 'Transactions created — last 6 months' : 'Leads created — last 6 months'}
              </div>
            </div>
            <TrendingUp size={18} className="text-brand-accent" />
          </div>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={data.monthly}>
              <defs>
                <linearGradient id="gline" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor={BRAND.accent} />
                  <stop offset="100%" stopColor={BRAND.accentSoft} />
                </linearGradient>
              </defs>
              <XAxis dataKey="month" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
              <Line type="monotone" dataKey="count" stroke="url(#gline)" strokeWidth={3} dot={{ fill: BRAND.accent, r: 5 }} activeDot={{ r: 7 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Pipeline breakdown */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
          <div className="font-serif font-semibold text-lg mb-1 text-brand-navy">Pipeline Stages</div>
          <div className="text-xs text-slate-500 mb-3">
            {effectiveProductType === 'real_estate' ? 'Transactions by stage' : 'Deals by stage'}
          </div>
          {/* Ticket 76: Recharts thins category ticks by default, which dropped every
              other stage label. interval={0} keeps them all; the chart grows with the
              stage count so rows never overlap, and long labels truncate (full name in
              the tooltip). */}
          <ResponsiveContainer width="100%" height={Math.max(220, stageData.length * 28)}>
            <BarChart data={stageData} layout="vertical" margin={{ left: 8, right: 16 }}>
              <XAxis type="number" hide />
              <YAxis dataKey="name" type="category" stroke="#94a3b8" fontSize={11} width={130} interval={0}
                tickFormatter={(v) => (String(v).length > 18 ? `${String(v).slice(0, 17)}…` : v)} />
              <Tooltip contentStyle={{ borderRadius: 12, border: '1px solid #e2e8f0' }} />
              <Bar dataKey="value" radius={[0, 8, 8, 0]}>
                {stageData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent items */}
      <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100">
        <div className="flex items-center justify-between mb-4">
          <div>
            <div className="font-serif font-semibold text-lg text-brand-navy" data-testid="recent-title">{c.recent_title || 'Recent Items'}</div>
            <div className="text-xs text-slate-500">{c.recent_subtitle || 'Most recently updated'}</div>
          </div>
          <Link to={c.view_all_href || '/portal/pipeline'} className="text-sm font-semibold flex items-center gap-1 hover:opacity-70 text-brand-accent">
            View all <ArrowUpRight size={14} />
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            <Users size={32} className="mx-auto mb-2 opacity-40" />
            <p>{c.empty_msg || 'No items yet.'}</p>
            <Button asChild variant="accent" className="mt-4">
              <Link to={c.add_href || '/portal/pipeline'}>{c.add_label || 'Add'}</Link>
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((l) => {
              // Both modes send { id, name, email, amount, status, href }; a row with an
              // href is a real link, one without stays plain.
              const status = l.status;
              const tint = labelColors[status] || stageColors[status] || '#94a3b8';
              const statusText = data.stage_bars ? status : (stageLabels[status] || status);
              const Row = l.href ? Link : 'div';
              return (
                <Row
                  key={l.id}
                  {...(l.href ? { to: l.href } : {})}
                  className={`flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors${l.href ? ' focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-accent' : ''}`}
                  data-testid="recent-row"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm flex-shrink-0" style={{ background: BRAND.navy, color: BRAND.accentSoft }}>
                      {l.name?.[0]?.toUpperCase() || 'L'}
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-sm truncate text-brand-navy">{l.name}</div>
                      <div className="text-xs text-slate-500 truncate">{l.email || '—'}</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-semibold text-brand-navy">{fmt$(l.amount)}</span>
                    <span className="text-xs px-2 py-1 rounded-full font-semibold uppercase tracking-wider" style={{ background: tint + '20', color: tint }}>
                      {statusText}
                    </span>
                  </div>
                </Row>
              );
            })}
          </div>
        )}
      </div>
    </PageContainer>
  );
}
